import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, CheckCircle2, X, ArrowRight, Minus } from 'lucide-react';

const ComparePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-[#4A90E2] border-b-2 border-[#4A90E2]">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            One App vs <span className="text-[#4A90E2]">Three</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Why pay for multiple tools when Winglet does it all?
          </p>
        </div>
      </section>

      {/* Comparison Table - Desktop */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="hidden lg:block overflow-x-auto">
            <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="p-6 text-left">
                      <span className="text-gray-400">Feature</span>
                    </th>
                    <th className="p-6 bg-gradient-to-br from-[#4A90E2]/20 to-[#1A2B4A] border-x-2 border-[#4A90E2]">
                      <div className="text-center">
                        <div className="inline-block bg-[#4A90E2] text-white px-4 py-1 rounded-full text-xs font-semibold mb-2">
                          RECOMMENDED
                        </div>
                        <p className="text-2xl font-heading font-bold text-white">Winglet</p>
                        <p className="text-sm text-gray-400">All-in-one</p>
                        <p className="text-xl font-semibold text-[#4A90E2] mt-2">$14.99/mo</p>
                      </div>
                    </th>
                    <th className="p-6 text-center">
                      <p className="text-xl font-heading font-bold text-white">Widget Crew</p>
                      <p className="text-sm text-gray-400">Pay calculator only</p>
                      <p className="text-lg font-semibold text-white mt-2">$9.99/mo</p>
                    </th>
                    <th className="p-6 text-center">
                      <p className="text-xl font-heading font-bold text-white">Flight Crew View</p>
                      <p className="text-sm text-gray-400">Scheduling only</p>
                      <p className="text-lg font-semibold text-white mt-2">Free</p>
                    </th>
                    <th className="p-6 text-center">
                      <p className="text-xl font-heading font-bold text-white">Flighty</p>
                      <p className="text-sm text-gray-400">Flight tracking</p>
                      <p className="text-lg font-semibold text-white mt-2">Free*</p>
                      <p className="text-xs text-gray-500">*with ads</p>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <FeatureRow 
                    category="Pay Protection"
                    feature="Automated Pay Variance Detection"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="PWA Credit Calculator (DPM, ADG, MCD)"
                    winglet={true}
                    widget={true}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Reroute Protection (Section 23L)"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="RED/YELLOW/GREEN Reroute Alerts"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Timecard Audit & Grievance Export"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  
                  <FeatureRow 
                    category="Compliance & Safety"
                    feature="FAR 117 Predictive Compliance"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Duty Period Analysis"
                    winglet={true}
                    widget={true}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Rest Period Tracking"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  
                  <FeatureRow 
                    category="Flight Tracking"
                    feature="Real-Time OOOI Updates"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={true}
                  />
                  <FeatureRow 
                    feature="Gate & Terminal Tracking"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={true}
                  />
                  <FeatureRow 
                    feature="Delay & Cancellation Alerts"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={true}
                  />
                  
                  <FeatureRow 
                    category="Scheduling"
                    feature="Rotation Import (PDF/ICS)"
                    winglet={true}
                    widget={true}
                    flightCrew={true}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Calendar Sync"
                    winglet={true}
                    widget={false}
                    flightCrew={true}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Spillover Credit Allocation"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  
                  <FeatureRow 
                    category="Platform & Support"
                    feature="Desktop Web App"
                    winglet={true}
                    widget={true}
                    flightCrew={true}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Mobile Responsive"
                    winglet={true}
                    widget="limited"
                    flightCrew={true}
                    flighty={true}
                  />
                  <FeatureRow 
                    feature="Email Support"
                    winglet={true}
                    widget={false}
                    flightCrew={false}
                    flighty={false}
                  />
                  <FeatureRow 
                    feature="Ad-Free Experience"
                    winglet={true}
                    widget={true}
                    flightCrew={true}
                    flighty={false}
                  />
                  
                  <FeatureRow 
                    category="Value"
                    feature="Error Detection"
                    winglet="Automatic"
                    widget="Manual"
                    flightCrew="None"
                    flighty="None"
                    isText={true}
                  />
                  <FeatureRow 
                    feature="Break-Even Point"
                    winglet="1 error/year"
                    widget="2-3 errors/year"
                    flightCrew="N/A"
                    flighty="N/A"
                    isText={true}
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Comparison Cards */}
          <div className="lg:hidden space-y-6">
            <ComparisonCard 
              name="Winglet"
              price="$14.99/mo"
              subtitle="All-in-one solution"
              recommended={true}
              features={[
                "Pay variance detection",
                "Reroute protection (23L)",
                "FAR 117 predictive",
                "Real-time tracking",
                "Timecard audit",
                "Calendar sync",
                "Email support",
                "Ad-free"
              ]}
              savings="Automated detection"
            />
            <ComparisonCard 
              name="Widget Crew"
              price="$9.99/mo"
              subtitle="Basic pay only"
              features={[
                "PWA credit calculator",
                "Duty period analysis",
                "PDF import",
                "Desktop web app"
              ]}
              savings="Manual checking"
            />
            <ComparisonCard 
              name="Flight Crew View + Flighty"
              price="Free (with ads)"
              subtitle="Multiple apps needed"
              features={[
                "Schedule viewing",
                "Calendar sync",
                "Flight tracking",
                "Gate updates",
                "Ads on free tier"
              ]}
              savings="$0/mo"
            />
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            What Makes Winglet <span className="text-[#4A90E2]">Different</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <DifferentiatorCard 
              title="Only Tool with Reroute Engine"
              description="RED/YELLOW/GREEN alerts for Section 23L reroutes. No other app calculates pre-break vs post-break premiums."
              highlight="Detects reroute pay errors"
            />
            <DifferentiatorCard 
              title="Variance Detection = Auto-Audit"
              description="Upload your company timecard, we compare line-by-line. Widget Crew shows YOUR calculation, but doesn't catch THEIR errors."
              highlight="Most pilots catch 3-5 errors/year"
            />
            <DifferentiatorCard 
              title="FAR 117 Before You Accept"
              description="Predictive compliance checking. See violations BEFORE you accept the trip. Flighty only tracks after departure."
              highlight="Avoid illegal reroutes"
            />
          </div>
        </div>
      </section>

      {/* Why Not Just Use Free Tools */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            "Why not just use free tools?"
          </h2>
          
          <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Flight Crew View doesn't check pay</h3>
                  <p className="text-gray-300">
                    It's a great schedule viewer, but has no pay calculator, no variance detection, no reroute protection. You'd still need Widget Crew ($9.99) for basic pay.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Widget Crew doesn't catch company errors</h3>
                  <p className="text-gray-300">
                    Shows YOUR calculation, but no variance detection. No Section 23L reroute calculator. No FAR 117 predictive. No flight tracking. Costs $9.99 anyway.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Flighty doesn't help with pay or compliance</h3>
                  <p className="text-gray-300">
                    Beautiful flight tracker, but it's for passengers. No pay calculator, no reroute logic, no FAR 117. And the free tier has ads.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-gradient-to-r from-[#4A90E2]/20 to-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg">
              <h3 className="text-xl font-heading font-bold text-white mb-2">The Math:</h3>
              <p className="text-gray-300 mb-4">
                Free tools + Widget Crew = $9.99/mo + juggling 3 apps + still missing reroute protection & variance detection
              </p>
              <p className="text-[#4A90E2] font-semibold text-lg">
                Winglet = $14.99/mo + everything in one place + automatic error detection
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Extra $5/mo for complete automation. Worth it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Ready to Switch?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Try Winglet free for 1.5 months. Cancel anytime.
          </p>
          <Link 
            to="/signup" 
            className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-12 py-5 rounded-lg text-xl font-semibold transition-all transform hover:scale-105 inline-flex items-center group"
          >
            Start Free Trial
            <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-gray-400 mt-4">
            No credit card · Import your existing data · Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureRow = ({ category, feature, winglet, widget, flightCrew, flighty, isText = false }) => {
  const renderCell = (value) => {
    if (isText) return <span className="text-gray-300 font-semibold">{value}</span>;
    if (value === true) return <CheckCircle2 className="h-6 w-6 text-green-400 mx-auto" />;
    if (value === false) return <X className="h-6 w-6 text-gray-600 mx-auto" />;
    if (value === "limited") return <Minus className="h-6 w-6 text-amber-400 mx-auto" />;
    return <span className="text-gray-600">—</span>;
  };

  return (
    <tr className="border-b border-gray-700/50 hover:bg-[#1A2B4A]/30 transition-colors">
      <td className="p-4">
        {category && <p className="text-[#4A90E2] font-semibold text-xs uppercase mb-1">{category}</p>}
        <p className="text-white">{feature}</p>
      </td>
      <td className="p-4 text-center bg-[#1A2B4A]/20 border-x border-[#4A90E2]/10">
        {renderCell(winglet)}
      </td>
      <td className="p-4 text-center">{renderCell(widget)}</td>
      <td className="p-4 text-center">{renderCell(flightCrew)}</td>
      <td className="p-4 text-center">{renderCell(flighty)}</td>
    </tr>
  );
};

const ComparisonCard = ({ name, price, subtitle, recommended = false, features, savings }) => (
  <div className={`bg-[#1A2B4A] border rounded-xl p-6 ${recommended ? 'border-[#4A90E2] border-2' : 'border-[#4A90E2]/30'}`}>
    {recommended && (
      <div className="inline-block bg-[#4A90E2] text-white px-4 py-1 rounded-full text-xs font-semibold mb-4">
        RECOMMENDED
      </div>
    )}
    <h3 className="text-2xl font-heading font-bold text-white mb-1">{name}</h3>
    <p className="text-sm text-gray-400 mb-2">{subtitle}</p>
    <p className="text-xl font-semibold text-[#4A90E2] mb-4">{price}</p>
    
    <div className="space-y-2 mb-4">
      {features.map((feature, idx) => (
        <div key={idx} className="flex items-start space-x-2">
          <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
          <span className="text-gray-300 text-sm">{feature}</span>
        </div>
      ))}
    </div>
    
    <div className="pt-4 border-t border-gray-700">
      <p className="text-sm text-gray-400">Average monthly savings:</p>
      <p className="text-lg font-semibold text-white">{savings}</p>
    </div>
  </div>
);

const DifferentiatorCard = ({ title, description, highlight }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6">
    <h3 className="text-xl font-heading font-bold text-white mb-3">{title}</h3>
    <p className="text-gray-300 mb-4">{description}</p>
    <div className="bg-[#4A90E2]/10 border border-[#4A90E2]/30 rounded-lg p-3">
      <p className="text-[#4A90E2] font-semibold text-sm">{highlight}</p>
    </div>
  </div>
);

export default ComparePage;
